import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

interface UsePledgeCourseDrawerTourGuideProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Whether a course is selected (drawer has content) */
  hasCourse: boolean;
}

/**
 * Tour Guide for Course Drawer (detail modal).
 * Only runs when drawer is open. Targets filters and buttons in the top section.
 */
export const usePledgeCourseDrawerTourGuide = ({
  isOpen,
  hasCourse,
}: UsePledgeCourseDrawerTourGuideProps) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isDrawerActive = isOpen && hasCourse && !isMobile;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeCourseDrawer",
      enabled: isDrawerActive,
      steps: [
        {
          id: "pledge-course-drawer-enrollment-filter",
          targetSelector:
            '[data-tour-item="pledge-course-drawer-enrollment-filter"]',
          title: t("tourGuide.pledgeCourseDrawer.enrollmentFilter.title"),
          description: t("tourGuide.pledgeCourseDrawer.enrollmentFilter.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-course-drawer-department-filter",
          targetSelector:
            '[data-tour-item="pledge-course-drawer-department-filter"]',
          title: t("tourGuide.pledgeCourseDrawer.departmentFilter.title"),
          description: t("tourGuide.pledgeCourseDrawer.departmentFilter.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-course-drawer-role-filter",
          targetSelector: '[data-tour-item="pledge-course-drawer-role-filter"]',
          title: t("tourGuide.pledgeCourseDrawer.roleFilter.title"),
          description: t("tourGuide.pledgeCourseDrawer.roleFilter.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-course-drawer-download-csv",
          targetSelector: '[data-tour-item="pledge-course-drawer-download-csv"]',
          title: t("tourGuide.pledgeCourseDrawer.downloadCsv.title"),
          description: t("tourGuide.pledgeCourseDrawer.downloadCsv.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-course-drawer-export-certificates",
          targetSelector:
            '[data-tour-item="pledge-course-drawer-export-certificates"]',
          title: t("tourGuide.pledgeCourseDrawer.exportCertificates.title"),
          description: t("tourGuide.pledgeCourseDrawer.exportCertificates.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
      ],
    }),
    [isDrawerActive, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [{ type: TourGuideType.PledgeCourseDrawer, isDone: true }],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeCourseDrawerTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeCourseDrawer,
    isOnTourPath: isDrawerActive,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeCourseDrawerTourGuide.isActive) {
      setActiveTourModule("pledgeCourseDrawer");
      setCurrentTourStep(pledgeCourseDrawerTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeCourseDrawerTourGuide.isActive,
    pledgeCourseDrawerTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeCourseDrawerTourGuide,
    isMobile,
  };
};
